<script lang="ts">
    import { VpEqualizerIcon } from '@decathlon-experience/icons/svelte';
    import { createDialog, melt } from '@melt-ui/svelte';
    import { writable } from 'svelte/store';
    import { fade } from 'svelte/transition';
    import { onMount, onDestroy } from 'svelte';

    import cn from '$common/utils/cn';
    import viewport from '$saga/actions/useViewport';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import FiltersBar from '$saga/views/Search/MainFilters/FiltersBar.svelte';
    import FiltersDrawerContent from '$saga/views/Search/MainFilters/FiltersDrawerContent.svelte';
    import {
        areMainFiltersActive,
        filtersContext,
    } from '$saga/views/Search/store';
    import { FilterType } from '$saga/views/Search/types';
    import Sort from '$saga/views/Search/Sort.svelte';
    import FiltersBarHome from '$saga/views/Search/MainFilters/FiltersBarHome.svelte';

    export let sortRef: HTMLInputElement | null = null;

    let isSticky = false;
    let selectedFilterType = FilterType.SELECTION;
    let isModalOpen = writable(false);
    let menuTitle = 'Rechercher';
    let drawerContentRef: FiltersDrawerContent | null = null;
    let lastScrollTop = 0;
    let isHidden = false;
    let mainFiltersButton: HTMLDivElement | null = null;
    let mainFiltersBarOriginalTop = 0;

    const handleScroll = () => {
        if (!mainFiltersButton) return;

        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

        // Only apply hide/show logic if we've scrolled past the original position
        // This ensures the bar is already sticky before we start hiding it
        if (scrollTop > mainFiltersBarOriginalTop) {
            isHidden = scrollTop > lastScrollTop;
        } else {
            // We're above the original position, always show
            isHidden = false;
        }

        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };

    onMount(() => {
        // Store the original position of the mainFiltersBar
        if (mainFiltersButton) {
            mainFiltersBarOriginalTop = mainFiltersButton.offsetTop + 88; // 88px accounts for the height of the sticky bar
        }
        window.addEventListener('scroll', handleScroll, { passive: true });
    });

    onDestroy(() => {
        window.removeEventListener('scroll', handleScroll);
    });

    const handleSelectedFilter = async (filterType: FilterType) => {
        selectedFilterType = filterType;
        switch (filterType) {
            case FilterType.SELECTION:
                menuTitle = 'Rechercher';
                break;
            case FilterType.DESTINATION:
                menuTitle = 'Rechercher une destination';
                break;
            case FilterType.SPORT:
                menuTitle = 'Rechercher un sport';
                break;
            case FilterType.PAX:
                menuTitle = 'Voyageurs';
                break;
            case FilterType.STARTDATE:
                menuTitle = 'Date de départ';
                break;
        }
    };

    const resetView = () => {
        selectedFilterType = FilterType.SELECTION;
        menuTitle = 'Rechercher';
    };

    const onDialogOutsideClick = () => {
        if (!$filtersContext.isHomePage && $filtersContext.isDirty) {
            drawerContentRef?.validateSelectionOnOutsideClick();
            // PLP only
            // auto submit form
            // when the user clicks outside the modal
            const form = document.getElementById('travel-search-engine-form');
            if (form) {
                setTimeout(() => {
                    form.dispatchEvent(
                        new Event('submit', {
                            bubbles: true,
                            cancelable: true,
                        }),
                    );
                }, 0);
            }
        }
    };

    const {
        elements: { trigger, title, overlay, content, close, portalled },
        states: { open },
    } = createDialog({
        open: isModalOpen,
        forceVisible: true,
        onOutsideClick: () => onDialogOutsideClick(),
    });
</script>

<div
    use:viewport
    on:enterViewport={() => {
        if (!$filtersContext.isHomePage) isSticky = false;
    }}
    on:exitViewport={() => {
        if (!$filtersContext.isHomePage) isSticky = true;
    }}
/>
{#if isSticky && !$filtersContext.isHomePage}
    <div
        class="desktop:hidden"
        style="height: {mainFiltersButton?.clientHeight ?? '88'}px"
    />
{/if}

{#if !$filtersContext.isHomePage}
    <div
        bind:this={mainFiltersButton}
        class={cn(
            'relative flex gap-xs p-l transition-transform duration-300 desktop:hidden',
            isSticky && 'fixed top-0 z-10 w-full bg-container-quiet',
            isHidden && '-translate-y-full',
        )}
    >
        <button
            class={cn(
                'relative top-0 z-20 flex h-12 w-full transform items-center justify-center gap-m rounded-full px-l py-s',
                'border border-solid border-border-neutral bg-white',
            )}
            use:melt={$trigger}
        >
            <span class="flex items-center gap-xs">
                <VpEqualizerIcon class="size-l" />
                <span>Filtrer</span>
                {#if $areMainFiltersActive}
                    <div class="h-xl w-fit">
                        <div
                            class="mb-auto size-[7px] rounded-full bg-blue-brand"
                        ></div>
                    </div>
                {/if}
            </span>
        </button>
        <Sort {sortRef} />
    </div>
{/if}

<!-- TODO MOBILE ONLY FOR NOW-->
{#if $filtersContext.isHomePage}
    <FiltersBarHome {trigger} {handleSelectedFilter} />
{/if}

<FiltersBar {trigger} {handleSelectedFilter} />

{#if $open}
    <div use:melt={$portalled}>
        <div
            use:melt={$overlay}
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        />
        <div
            use:melt={$content}
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <FiltersDrawerContent
                bind:this={drawerContentRef}
                {selectedFilterType}
                {handleSelectedFilter}
                {menuTitle}
                {title}
                {close}
                {resetView}
            />
        </div>
    </div>
{/if}
